// Yeast Market — commodity arbitrage. Prices walk randomly with a small bias
// toward bakers you own (more apprentices = higher flour price).
import { COMMODITIES, MARKET_HOLDING_CAP, MARKET_PRICE_UPDATE_MS } from '../content/commodities.js';
import { state, save } from '../systems/state.js';
import { fmt } from '../systems/util.js';
import { toast } from './toast.js';

export function updateMarketPrices() {
  const now = Date.now();
  if (now - (state.market.lastUpdateAt || 0) < MARKET_PRICE_UPDATE_MS) return;
  state.market.lastUpdateAt = now;
  for (const c of COMMODITIES) {
    // Random walk ±50%, plus +5% per 10 of the influencing baker (more bakers
    // = your supply is plentiful, market price is higher when you sell).
    const noise = 0.5 + Math.random();
    const ownedCount = state.owned[c.bakerInfluence] || 0;
    const influence = 1 + 0.05 * (ownedCount / 10);
    state.market.prices[c.id] = Math.max(1, Math.floor(c.basePrice * noise * influence));
  }
}

function buyCommodity(id) {
  const c = COMMODITIES.find(x => x.id === id);
  if (!c) return;
  const price = state.market.prices[id] || c.basePrice;
  const held = state.market.holdings[id] || 0;
  if (held >= MARKET_HOLDING_CAP) { toast(`max holdings (${MARKET_HOLDING_CAP}) reached`); return; }
  if (state.count < price) { toast(`not enough pumpernickels`); return; }
  state.count -= price;
  state.market.holdings[id] = held + 1;
  save(); renderMarket();
}

function sellCommodity(id) {
  const c = COMMODITIES.find(x => x.id === id);
  if (!c) return;
  const price = state.market.prices[id] || c.basePrice;
  const held = state.market.holdings[id] || 0;
  if (held <= 0) return;
  state.market.holdings[id] = held - 1;
  state.count += price;
  // No lifetime credit on sale — without cost-basis tracking, crediting could
  // inflate lifetime via buy/sell-same-tick loops. state.count is the truth.
  save(); renderMarket();
}

function buyMaxCommodity(id) {
  const c = COMMODITIES.find(x => x.id === id);
  if (!c) return;
  const price = state.market.prices[id] || c.basePrice;
  const held = state.market.holdings[id] || 0;
  const slots = MARKET_HOLDING_CAP - held;
  const affordable = Math.floor(state.count / price);
  const qty = Math.min(slots, affordable);
  if (qty <= 0) return;
  state.count -= qty * price;
  state.market.holdings[id] = held + qty;
  save(); renderMarket();
}

function sellAllCommodity(id) {
  const c = COMMODITIES.find(x => x.id === id);
  if (!c) return;
  const price = state.market.prices[id] || c.basePrice;
  const held = state.market.holdings[id] || 0;
  if (held <= 0) return;
  state.count += held * price;
  state.market.holdings[id] = 0;
  save(); renderMarket();
}

export function renderMarket() {
  const list = document.getElementById('market-list');
  if (!list) return;
  updateMarketPrices();
  list.innerHTML = '';
  for (const c of COMMODITIES) {
    const price = state.market.prices[c.id] || c.basePrice;
    const held = state.market.holdings[c.id] || 0;
    const baseline = c.basePrice;
    const delta = ((price / baseline - 1) * 100).toFixed(0);
    const trend = price > baseline ? `<span style="color:#cfe9c9">+${delta}%</span>` : `<span style="color:#f0c4c4">${delta}%</span>`;
    const row = document.createElement('div');
    row.className = 'market-row';
    row.innerHTML = `
      <div class="market-icon">${c.icon}</div>
      <div class="market-info">
        <div class="market-name">${c.name}</div>
        <div class="market-meta">price ${fmt(price)} · ${trend} · holding ${held}/${MARKET_HOLDING_CAP}</div>
      </div>
      <div class="market-actions">
        <button data-act="buy"     data-id="${c.id}" ${state.count < price || held >= MARKET_HOLDING_CAP ? 'disabled' : ''}>buy</button>
        <button data-act="buy-max" data-id="${c.id}" ${state.count < price || held >= MARKET_HOLDING_CAP ? 'disabled' : ''}>max</button>
        <button data-act="sell"    data-id="${c.id}" ${held <= 0 ? 'disabled' : ''}>sell</button>
        <button data-act="sell-all" data-id="${c.id}" ${held <= 0 ? 'disabled' : ''}>all</button>
      </div>
    `;
    list.appendChild(row);
  }
  list.querySelectorAll('button[data-act]').forEach(btn => {
    btn.addEventListener('click', () => {
      const act = btn.dataset.act, id = btn.dataset.id;
      if (act === 'buy') buyCommodity(id);
      else if (act === 'buy-max') buyMaxCommodity(id);
      else if (act === 'sell') sellCommodity(id);
      else if (act === 'sell-all') sellAllCommodity(id);
    });
  });
  const sub = document.getElementById('market-sub');
  if (sub) {
    const next = Math.max(0, MARKET_PRICE_UPDATE_MS - (Date.now() - (state.market.lastUpdateAt || 0)));
    sub.textContent = `prices refresh every 30s. owned bakers raise the matching commodity price. next refresh ${Math.ceil(next/1000)}s.`;
  }
}
